import { useEffect } from 'react';

import { Loader2 } from 'lucide-react';
import { Controller } from 'react-hook-form';

import type { ClientId, SessionChargeId } from '@nesso/ledger-contracts/ids';
import { Button } from '@nesso/ui/primitives/button';
import { FieldError } from '@nesso/ui/primitives/field';
import { InputField } from '@nesso/ui/primitives/input-field';
import { SheetBody, SheetFooter } from '@nesso/ui/primitives/sheet';

import type { UnpaidSessionCharge } from '@/entities/session-charge';
import { formatCurrencyFromCents } from '@/shared/lib/format-utils';

import { useRecordPaymentForm } from '../../../model/use-record-payment-form';
import { RecordPaymentSheetDate } from './record-payment-sheet-form/record-payment-sheet-date';
import { RecordPaymentSheetMethod } from './record-payment-sheet-form/record-payment-sheet-method';
import { RecordPaymentSheetServices } from './record-payment-sheet-form/record-payment-sheet-services';

type Props = {
    clientId: ClientId;
    unpaid: UnpaidSessionCharge[];
    selectedSessionChargeId: SessionChargeId | null;
    onClose: () => void;
    onPendingChange: (pending: boolean) => void;
};

export function RecordPaymentSheetForm({
    clientId,
    unpaid,
    selectedSessionChargeId,
    onClose,
    onPendingChange,
}: Props) {
    const recordPayment = useRecordPaymentForm({
        clientId,
        unpaid,
        selectedSessionChargeId,
        onSuccess: onClose,
    });
    const { control, formState } = recordPayment.form;
    const controlsDisabled = recordPayment.isPending || recordPayment.isLocked;

    useEffect(() => {
        onPendingChange(recordPayment.isPending);
        return () => onPendingChange(false);
    }, [recordPayment.isPending, onPendingChange]);

    return (
        <form onSubmit={recordPayment.onSubmit} className="flex min-h-0 flex-1 flex-col">
            <SheetBody className="flex flex-1 flex-col gap-6 overflow-y-auto pt-4">
                <RecordPaymentSheetServices
                    unpaid={unpaid}
                    selectedIds={recordPayment.selectedIds}
                    totalCents={recordPayment.totalCents}
                    disabled={controlsDisabled}
                    onToggleSession={recordPayment.toggleSession}
                    onToggleAllSessions={recordPayment.toggleAllSessions}
                />
                <RecordPaymentSheetMethod
                    value={recordPayment.method}
                    error={formState.errors.method?.message}
                    disabled={controlsDisabled}
                    onValueChange={recordPayment.selectMethod}
                >
                    <div className="flex items-start gap-3">
                        <RecordPaymentSheetDate
                            value={recordPayment.paidDate}
                            disabled={controlsDisabled}
                            onChange={recordPayment.selectPaidDate}
                        />
                        {recordPayment.method === 'CHECK' && (
                            <Controller
                                control={control}
                                name="checkNumber"
                                render={({ field, fieldState }) => (
                                    <InputField
                                        name={field.name}
                                        ref={field.ref}
                                        id="record-payment-check-number"
                                        label="Check No"
                                        fieldClassName="w-[127px]"
                                        value={field.value ?? ''}
                                        onChange={(event) =>
                                            field.onChange(event.target.value || undefined)
                                        }
                                        onBlur={field.onBlur}
                                        error={fieldState.error?.message ?? fieldState.invalid}
                                        disabled={controlsDisabled}
                                    />
                                )}
                            />
                        )}
                    </div>
                </RecordPaymentSheetMethod>
                {formState.errors.root && <FieldError>{formState.errors.root.message}</FieldError>}
            </SheetBody>
            <SheetFooter>
                <Button
                    type="button"
                    variant="ghost"
                    disabled={recordPayment.isPending}
                    onClick={onClose}
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    disabled={recordPayment.isPending || recordPayment.selectedIds.size === 0}
                >
                    {recordPayment.isPending && <Loader2 className="animate-spin" />}
                    {recordPayment.selectedIds.size === 0
                        ? 'Add Payment'
                        : `Add ${formatCurrencyFromCents(recordPayment.totalCents)} Payment`}
                </Button>
            </SheetFooter>
        </form>
    );
}
